import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { debounceByKey } from '@/shared/lib/performance';
import { anglesService } from '@/shared/api/services/angles.service';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { JsonApiResponse } from '@/shared/api/services';
import type { Concept, ConceptAngle } from '@/types/concept';

interface ConceptState {
  // Mobile header data — project-level only. Concept-level fields
  // (conceptId, conceptSequenceNumber, conceptTitle, isFavorite,
  // prevConceptId, nextConceptId) are derived from `useConcept()` in
  // `useConceptHeaderData()` rather than mirrored here.
  mobileHeader: {
    projectId: string | null;
    projectName: string | null;
  };

  // UI state
  ui: {
    showRenders: Record<string, boolean>; // conceptAngleId -> show render or source
    selectedRenderId: Record<string, string>; // conceptAngleId -> selected render ID

    // Modal states
    showMainAngleUpload: boolean;
    showMainAngleReplace: boolean;
    showDerivativeUploadModal: boolean;
    showDeleteRenderDialog: boolean;

    // Form states
    isValidating: boolean;
    validationError: string | null;
    error: string | null;

    // Replace angle data
    replaceAngleData: {
      conceptAngleId: string;
      angleName: string;
      hasRenders: boolean;
    } | null;

    // Delete render data
    renderToDelete: {
      angleId: string;
      renderId: string;
    } | null;
  };

  // Actions
  setMobileHeaderData: (data: Partial<ConceptState['mobileHeader']>) => void;
  loadSelectedRenderIds: (conceptAngles: ConceptAngle[]) => void;

  // UI actions
  toggleShowRender: (conceptAngleId: string) => void;
  setSelectedRenderId: (
    conceptAngleId: string,
    renderId: string,
    conceptId: string | undefined
  ) => void;
  /** Update selection from remote event — no API sync to avoid echo */
  setSelectedRenderIdFromRemote: (
    conceptAngleId: string,
    renderId: string
  ) => void;
  removeSelectedRenderId: (conceptAngleId: string) => void;
  /** Cancel any debounced selected-render API sync for this angle. Call when the
   *  underlying angle/concept goes away so the deferred PATCH can't fire and
   *  resurrect deleted state. */
  cancelPendingSelection: (conceptAngleId: string) => void;
  /** Cancel every pending selected-render sync WITHOUT firing it. For a hard
   *  teardown (e.g. logout) where deferred PATCHes must be dropped, not flushed.
   *  The unmount path uses `reset()`, which flushes instead. */
  cancelAllPendingSelections: () => void;
  openModal: (modal: keyof ConceptState['ui']) => void;
  closeModal: (modal: keyof ConceptState['ui']) => void;
  setError: (error: string | null) => void;
  setValidationError: (error: string | null) => void;
  setReplaceAngleData: (data: ConceptState['ui']['replaceAngleData']) => void;
  setRenderToDelete: (data: ConceptState['ui']['renderToDelete']) => void;

  // Reset
  reset: () => void;
}

// Tracks angles with pending (debounced) selection syncs.
// While pending, incoming WebSocket selection events are suppressed to prevent carousel jump-back.
const pendingSelections = new Set<string>();

// Per-angle debounced API sync for selected render (3 second delay per angle)
// Each angle has its own debounce timer to prevent cross-angle cancellations
const syncSelectedRenderToApi = debounceByKey(
  (conceptAngleId: string, conceptId: string, renderId: string) => {
    void anglesService
      .updateSelectedRender(conceptId, conceptAngleId, renderId)
      .catch((error) => {
        console.error(
          `[Store] Failed to sync selected render to API for angle ${conceptAngleId}:`,
          error
        );
      })
      .finally(() => {
        pendingSelections.delete(conceptAngleId);
      });
  },
  3000
);

/** Check if an angle has a pending selection sync (debounce in progress) */
export function hasPendingSelection(conceptAngleId: string): boolean {
  return pendingSelections.has(conceptAngleId);
}

// Both the concept page (`cacheKeys.concepts.detail`) and the concepts-overview
// grid (`cacheKeys.concepts.overview`) cache the same `getConceptWithIncludes`
// envelope and read `selected_render_id` off the included `concept_angles`.
// Write-through must patch BOTH so the grid thumbnail stays in sync with the
// page carousel — mirrors Apple's note that ConceptOverviewCard reads the same
// snapshot.
const CONCEPT_SNAPSHOT_PREFIXES = [
  cacheKeys.concepts.details(),
  ['concept-overview-details'],
] as const;

/**
 * Write the chosen render id through to every cached concept snapshot holding
 * the angle.
 *
 * Selection is a single fact that must live in all three tiers: memory
 * (`ui.selectedRenderId`), the server (debounced PATCH), and the local cache.
 * The cache write is what survives a fast concept switch — `ConceptEditor`
 * remounts per concept (`key={conceptId}`) and re-seeds from this cached
 * `selected_render_id` via `loadSelectedRenderIds`, so without the write-through
 * a switch-away-and-back inside the 5-min query staleTime would paint the
 * PREVIOUS render until the server round-trip lands. Mirrors the Apple
 * (`updateCachedSelectedRender`) / Android (`persistSelectedRenderToCache`)
 * cache write-through. Removing this resurrects the "tail" flash on switch-back.
 *
 * Scans by angle id (not concept id) so the WS path — which only carries the
 * angle — patches the same snapshots as the local edit path.
 */
function persistSelectedRenderToCache(
  conceptAngleId: string,
  renderId: string
): void {
  const patch = (
    prev: JsonApiResponse<Concept> | undefined
  ): JsonApiResponse<Concept> | undefined => {
    if (!prev?.included) return prev;
    let changed = false;
    const included = prev.included.map((item) => {
      const resource = item as {
        type?: string;
        id?: string;
        attributes?: { selected_render_id?: string | null };
      };
      if (
        resource.type !== 'concept_angles' ||
        resource.id !== conceptAngleId
      ) {
        return item;
      }
      if (resource.attributes?.selected_render_id === renderId) return item;
      changed = true;
      return {
        ...resource,
        attributes: { ...resource.attributes, selected_render_id: renderId },
      };
    });
    if (!changed) return prev;
    return { ...prev, included };
  };

  for (const prefix of CONCEPT_SNAPSHOT_PREFIXES) {
    const entries = queryClient.getQueriesData<JsonApiResponse<Concept>>({
      queryKey: prefix,
    });
    for (const [key, data] of entries) {
      const holdsAngle = data?.included?.some(
        (item) =>
          (item as { type?: string; id?: string }).type === 'concept_angles' &&
          (item as { type?: string; id?: string }).id === conceptAngleId
      );
      if (holdsAngle) {
        queryClient.setQueryData<JsonApiResponse<Concept>>(key, patch);
      }
    }
  }
}

const initialUIState: ConceptState['ui'] = {
  showRenders: {},
  selectedRenderId: {},
  showMainAngleUpload: false,
  showMainAngleReplace: false,
  showDerivativeUploadModal: false,
  showDeleteRenderDialog: false,
  isValidating: false,
  validationError: null,
  error: null,
  replaceAngleData: null,
  renderToDelete: null,
};

export const useConceptStore = create<ConceptState>()(
  subscribeWithSelector(
    immer((set) => ({
      // Initial state
      mobileHeader: {
        projectId: null,
        projectName: null,
      },
      ui: initialUIState,

      setMobileHeaderData: (data) =>
        set((state) => {
          Object.assign(state.mobileHeader, data);
        }),

      loadSelectedRenderIds: (conceptAngles) =>
        set((state) => {
          const selectedIds: Record<string, string> = {};

          conceptAngles.forEach((conceptAngle) => {
            // Preserve local selection while debounce is pending — user's choice takes priority
            if (pendingSelections.has(conceptAngle.id)) {
              const localSelection =
                state.ui.selectedRenderId[conceptAngle.id];
              if (localSelection) {
                selectedIds[conceptAngle.id] = localSelection;
                return;
              }
            }
            if (conceptAngle.attributes.selected_render_id) {
              selectedIds[conceptAngle.id] =
                conceptAngle.attributes.selected_render_id;
            }
          });

          state.ui.selectedRenderId = selectedIds;
        }),

      // UI actions
      toggleShowRender: (conceptAngleId) =>
        set((state) => {
          state.ui.showRenders[conceptAngleId] =
            !state.ui.showRenders[conceptAngleId];
        }),

      setSelectedRenderId: (conceptAngleId, renderId, conceptId) =>
        set((state) => {
          state.ui.selectedRenderId[conceptAngleId] = renderId;

          // Sync to API (per-angle debounced) - but skip temporary render IDs from Store
          // Temporary IDs have format: temp-{jobId}. A temp id has no server-side
          // render to point at, so it's written to memory only (the real id
          // replaces it once the render lands and re-runs this with a real id).
          const isTemporaryId = renderId.startsWith('temp-');
          if (conceptId && !isTemporaryId) {
            // Write-through to cache BEFORE the debounce so a fast nav-away still
            // leaves the cached snapshot consistent for the next re-seed.
            persistSelectedRenderToCache(conceptAngleId, renderId);
            pendingSelections.add(conceptAngleId);
            syncSelectedRenderToApi(conceptAngleId, conceptId, renderId);
          }
        }),

      setSelectedRenderIdFromRemote: (conceptAngleId, renderId) =>
        set((state) => {
          state.ui.selectedRenderId[conceptAngleId] = renderId;
          // No API sync — this came from the server. Still write through to the
          // local cache so a later re-seed (concept switch) keeps the
          // server-pushed selection instead of reverting to a stale snapshot.
          persistSelectedRenderToCache(conceptAngleId, renderId);
        }),

      removeSelectedRenderId: (conceptAngleId) => {
        // Kill any in-flight debounce — otherwise the 3s deferred PATCH
        // resurrects a selection for an angle we just deleted.
        syncSelectedRenderToApi.cancel(conceptAngleId);
        pendingSelections.delete(conceptAngleId);
        set((state) => {
          delete state.ui.selectedRenderId[conceptAngleId];
        });
      },

      cancelPendingSelection: (conceptAngleId) => {
        syncSelectedRenderToApi.cancel(conceptAngleId);
        pendingSelections.delete(conceptAngleId);
      },

      cancelAllPendingSelections: () => {
        syncSelectedRenderToApi.cancelAll();
        pendingSelections.clear();
      },

      openModal: (modal) =>
        set((state) => {
          const key = modal as keyof typeof state.ui;
          if (typeof state.ui[key] === 'boolean') {
            (state.ui[key] as boolean) = true;
          }
        }),

      closeModal: (modal) =>
        set((state) => {
          const key = modal as keyof typeof state.ui;
          if (typeof state.ui[key] === 'boolean') {
            (state.ui[key] as boolean) = false;
          }
        }),

      setError: (error) =>
        set((state) => {
          state.ui.error = error;
        }),

      setValidationError: (error) =>
        set((state) => {
          state.ui.validationError = error;
        }),

      setReplaceAngleData: (data) =>
        set((state) => {
          state.ui.replaceAngleData = data;
        }),

      setRenderToDelete: (data) =>
        set((state) => {
          state.ui.renderToDelete = data;
        }),

      // Reset — keep project-level mobile header during reset to avoid flashing.
      // FLUSH (not cancel) every pending selected-render debounce: ConceptEditor
      // remounts per concept (key={conceptId}), so a concept switch unmounts the
      // editor and runs reset() — cancelling here would silently drop the PATCH
      // whenever the user picks a render and pages away inside the 3s window
      // (the common case), so the choice would never reach the server. Each
      // debounced call captured its own conceptId at schedule time, so a flush
      // during a page-swap still PATCHes the correct (previous) concept.
      reset: () => {
        syncSelectedRenderToApi.flushAll();
        pendingSelections.clear();
        set((state) => ({
          mobileHeader: {
            projectId: state.mobileHeader.projectId,
            projectName: state.mobileHeader.projectName,
          },
          ui: initialUIState,
        }));
      },
    }))
  )
);
